import React, { useState, useMemo, useEffect } from 'react';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';

import Homepage from './pages/Homepage';
import Privacy from './components/privacy';
import Terms from './components/terms';
import WatchlistPage from './components/WatchlistPage';

import { AuthProvider } from './context/AuthContext';
import { ChatProvider } from './context/ChatContext';

// Build theme tokens with more sophisticated styling
const getDesignTokens = mode => ({
  palette: {
    mode,
    primary: {
      main: '#10a37f',
      light: '#34c5a0',
      dark: '#0c8164',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#4361ee',
      light: '#667eea',
      dark: '#3948ab',
      contrastText: '#ffffff',
    },
    background: {
      default: mode === 'light' ? '#f7f8fa' : '#121212',
      paper: mode === 'light' ? '#ffffff' : '#1e1e1e',
    },
    text: {
      primary: mode === 'light' ? '#171923' : '#e2e8f0',
      secondary: mode === 'light' ? '#4a5568' : '#a0aec0',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 700,
    },
    h6: {
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
  },
});

function App() {
  const [mode, setMode] = useState(() => {
    // Try to get theme preference from localStorage first
    const savedMode = localStorage.getItem('themeMode');
    return savedMode || 'light';
  });

  // sync with OS pref initially and store preference
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    
    // Only apply OS preference if no saved preference
    if (!localStorage.getItem('themeMode')) {
      setMode(mq.matches ? 'dark' : 'light');
    }
    
    // Listen for OS changes
    const apply = e => {
      if (!localStorage.getItem('themeMode')) {
        setMode(e.matches ? 'dark' : 'light');
      }
    };
    
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);

  // Save theme preference to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('themeMode', mode);
    
    // Apply theme class to body for global CSS
    document.body.classList.remove('light-mode', 'dark-mode');
    document.body.classList.add(`${mode}-mode`);
  }, [mode]);

  const theme = useMemo(() => createTheme(getDesignTokens(mode)), [mode]);
  
  const toggleMode = () => {
    setMode(m => {
      const newMode = m === 'light' ? 'dark' : 'light';
      return newMode;
    });
  };

  return (
    <AuthProvider>
      <ChatProvider>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<Homepage toggleMode={toggleMode} />} />
              <Route path="/watchlist" element={<WatchlistPage />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </ThemeProvider>
      </ChatProvider>
    </AuthProvider>
  );
}

export default App;